import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { TenancyActions } from './tenancy.actions';
import { TenancyService } from './tenancy.service';
import { tenancyFeature } from './tenancy.selectors';
import { MessageService } from '@letly/ui-common';
import { ApiResponse, InvoiceInitResponse, ToastSeverity } from '@letly/models';

declare let Plaid: any;

@Injectable()
export class TenancyEffects {

  private service = inject(TenancyService);
  private actions$ = inject(Actions);
  private store = inject(Store);
  private messageService = inject(MessageService);

  loadTenantTenancies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TenancyActions.loadTenantTenancies),
      withLatestFrom(this.store.select(tenancyFeature.selectTenantTenanciesFilters)),
      switchMap(([action, filters]) => {
        return this.service.getTenancies(action.filters || filters).pipe(
          map(response => {
            if (response.success) {
              return TenancyActions.onLoadTenantTenancies({
                status: 'success',
                message: '',
                response: response.data
              });
            }
            return TenancyActions.onLoadTenantTenancies({
              status: 'error',
              message: response.niceErrorMessage,
              response: null
            });
          })
        );
      })
    )
  );

  loadLandlordTenancies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TenancyActions.loadLandlordTenancies),
      withLatestFrom(this.store.select(tenancyFeature.selectLandlordTenanciesFilters)),
      switchMap(([action, filters]) => {
        return this.service.getLandlordTenancies(action.filters || filters).pipe(
          map(response => {
            if (response.success) {
              return TenancyActions.onLoadLandlordTenancies({
                status: 'success',
                message: '',
                response: response.data
              });
            }
            return TenancyActions.onLoadLandlordTenancies({
              status: 'error',
              message: response.niceErrorMessage,
              response: null
            });
          })
        );
      })
    )
  );

  loadTenancyDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TenancyActions.loadTenancyDetails),
      mergeMap(({ tenancyId }) => {

        return this.service.getTenancyDetail(tenancyId).pipe(
          map(response => {
            if (response.success) {
              return TenancyActions.onLoadTenancyDetails({
                status: 'success',
                message: '',
                tenancyId: tenancyId,
                tenancy: response.data
              });
            }
            return TenancyActions.onLoadTenancyDetails({
              status: 'error',
              message: response.niceErrorMessage || $localize`:@@tenancy.error.detail-load-failed:We couldn’t load your tenancy details. Please try again.`,
              tenancyId: tenancyId,
              tenancy: null
            });
          })
        );
      })
    )
  );

  loadDepositServiceRecords$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TenancyActions.loadDepositServiceRecords),
      mergeMap(({ tenancyId }) =>
        this.service.getDepositServiceRecords(tenancyId).pipe(
          map(response =>
            response.success
              ? TenancyActions.onLoadDepositServiceRecords({
                  status: 'success',
                  message: '',
                  tenancyId,
                  records: response.data
                })
              : TenancyActions.onLoadDepositServiceRecords({
                  status: 'error',
                  message: response.niceErrorMessage,
                  tenancyId,
                  records: []
                })
          )
        )
      )
    )
  );

  loadInvoices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TenancyActions.loadInvoices, TenancyActions.updateInvoiceFilters),
      mergeMap(({ tenancyId, filters }) => {
        const filters$ = filters ? of(filters) : this.store.select(tenancyFeature.selectInvoiceFilters(tenancyId));

        return filters$.pipe(
          switchMap(invoiceFilters =>
            this.service.getInvoice(tenancyId, invoiceFilters).pipe(
              map(response => {
                if (response.success) {
                  return TenancyActions.onLoadInvoices({
                    tenancyId,
                    status: 'success',
                    message: '',
                    response: response.data
                  });
                }
                return TenancyActions.onLoadInvoices({
                  tenancyId,
                  status: 'error',
                  message: response.niceErrorMessage || $localize`:@@tenancy.error.invoices-load-failed:We couldn’t load your invoices. Please try again.`,
                  response: null
                });
              })
            )
          )
        );
      })
    )
  );

  initializeInvoicePayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TenancyActions.initializeInvoicePayment),
      mergeMap(({ tenancyId, invoiceId }) =>
        this.service.initPayment(tenancyId, invoiceId).pipe(
          mergeMap(response => {
            if (!response.success) {
              const errorMessage = response.niceErrorMessage || $localize`:@@tenancy.error.payment-init-failed:We couldn’t start your payment. Please try again.`;
              this.messageService.showSnackbar(errorMessage, ToastSeverity.Error);

              return of(TenancyActions.onInitializeInvoicePayment({
                status: 'error',
                message: errorMessage,
                invoiceId,
                initResponse: null
              }));
            }

            return this.initiatePlaidLink(response).pipe(
              mergeMap(() => [
                TenancyActions.onInitializeInvoicePayment({
                  status: 'success',
                  message: '',
                  invoiceId,
                  initResponse: response.data
                }),
                TenancyActions.paymentCompleted({
                  tenancyId,
                  invoiceId
                })
              ]),
              catchError(plaidError => {
                console.error('Plaid initialization failed:', plaidError);
                return of(TenancyActions.onInitializeInvoicePayment({
                  status: 'success',
                  message: '',
                  invoiceId,
                  initResponse: response.data
                }));
              })
            );
          }),
          catchError(_error => {
            const errorMessage = $localize`:@@tenancy.error.payment-init-failed:We couldn’t start your payment. Please try again.`;
            this.messageService.showSnackbar(errorMessage, ToastSeverity.Error);

            return of(TenancyActions.onInitializeInvoicePayment({
              status: 'error',
              message: errorMessage,
              invoiceId,
              initResponse: null
            }));
          })
        )
      )
    )
  );

  refreshTenancyAfterPayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TenancyActions.paymentCompleted),
      map(({ tenancyId }) => TenancyActions.loadTenancyDetails({
        tenancyId,
      }))
    )
  );

  refreshInvoicesAfterPayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TenancyActions.paymentCompleted),
      map(({ tenancyId }) => TenancyActions.loadInvoices({
        tenancyId,
      }))
    )
  );

  initiatePlaidLink(response: ApiResponse<InvoiceInitResponse>): Observable<{
    metadata: any;
    public_token?: string;
    err?: any
  }> {
    return new Observable<{ metadata: any; public_token?: string; err?: any }>(observer => {
      const handler = Plaid.create({
        token: response.data.clientLink.token,
        onSuccess: (public_token, metadata) => {
          observer.next({ public_token, metadata });
          observer.complete();
        },
        onExit: (err, metadata) => {
          if (err) {
            observer.error({ err, metadata });
          }
          observer.complete();
        }
      });

      handler.open();
    });
  }
}
